import React, { useState, useEffect, useRef } from "react";
import "../css-dashboard/Dashboard.css";
import SidebarClient from "../../components/sidebar-dashboard/SidebarClient";
import KycVerification from "../../components/KycVerification/KycVerification";
import { DiditSdk } from "@didit-protocol/sdk-web";
import { useAuth } from "../../context/AuthContext";
import { useLocation } from "react-router-dom";
import {
  useJsApiLoader,
  GoogleMap,
  Polyline,
  DirectionsRenderer,
} from "@react-google-maps/api";
import QRCode from "react-qr-code";
import {
  CreditCard,
  User,
  Phone,
  Calendar,
  CalendarDays,
  CalendarCheck,
  Lock,
  Car,
  Clock,
  Briefcase,
  QrCode,
  Copy,
  CheckCircle2,
} from "lucide-react";
import UsePaymentServiceTripNegotiation from "../../hooks/UsePaymentServiceTripNegotiation/UsePaymentServiceTripNegotiation";

const API_URL = process.env.REACT_APP_API_URL;
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const defaultCenter = { lat: -15.7801, lng: -47.9292 };
const mapId = "DEMO_MAP_ID";

function buildStoredStop(stored, prefix) {
  const name = stored?.[`cidade_${prefix}`] || "";
  const lat = Number(stored?.[`lat_cidade_${prefix}`]);
  const lng = Number(stored?.[`lng_cidade_${prefix}`]);

  if (!name || Number.isNaN(lat) || Number.isNaN(lng)) return null;

  return {
    name,
    position: { lat, lng },
  };
}

function formatDurationPt(seconds = 0) {
  const totalMinutes = Math.max(0, Math.round(Number(seconds) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} hora${hours === 1 ? "" : "s"}`);
  }

  if (minutes > 0 || hours === 0) {
    parts.push(`${minutes} minuto${minutes === 1 ? "" : "s"}`);
  }

  return parts.join(" ");
}

function getRouteTotalDurationSeconds(route) {
  const legs = route?.legs || [];

  return legs.reduce(
    (acc, leg) => acc + (Number(leg?.duration?.value) || 0),
    0,
  );
}

function createMarkerContent(label) {
  const div = document.createElement("div");

  div.className = "custom-marker";
  div.textContent = label;

  return div;
}

const PaymentService = ({ menuOpen, setMenuOpen }) => {
  const location = useLocation();
  const { user, loadUser, authFetch } = useAuth();

  const kycVerified = user?.kyc_status === "APPROVED";

  const mapRef = useRef(null);
  const originMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);
  const storedTrip = location.state?.item?.trip || null;
  const isAirplane = storedTrip?.meio_transporte === "aviao";

  const [loadingKyc, setLoadingKyc] = useState(false);
  const [showKycWarningModal, setShowKycWarningModal] = useState(false);
  const [showKycResultModal, setShowKycResultModal] = useState(false);
  const [kycFinished, setKycFinished] = useState(false);
  const [kycResult, setKycResult] = useState(null);

  const [originPos, setOriginPos] = useState(null);
  const [destinationPos, setDestinationPos] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [directions, setDirections] = useState(null);
  const [routeOptions, setRouteOptions] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);

  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("credit_card");
  const [pixCopied, setPixCopied] = useState(false);
  const [showPix, setShowPix] = useState(false);

  const [cardHolderDifferent, setCardHolderDifferent] = useState(false);
  const [cardData, setCardData] = useState({
    number: "",
    holderName: "",
    expiry: "",
    cvv: "",
  });

  const [friendCardData, setFriendCardData] = useState({
    nome: "",
    cpf: "",
    telefone: "",
  });

  const {
    loadingPayment,
    paymentProcessing,
    paymentMessage,
    errorPayment,
    pixData,
    paymentService,
  } = UsePaymentServiceTripNegotiation(() => {
    setPaymentCompleted(true);
  });

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    language: "pt-BR",
    region: "BR",
  });

  useEffect(() => {
    async function fechar(event) {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type !== "DIDIT_KYC_DONE") return;

      const modal = document.querySelector(".didit-modal-overlay");

      if (modal) {
        modal.classList.remove("active");
        modal.style.display = "none";
      }

      await loadUser();

      setKycFinished(true);
    }

    window.addEventListener("message", fechar);

    return () => window.removeEventListener("message", fechar);
  }, [loadUser]);

  useEffect(() => {
    if (!kycFinished) return;

    if (user?.kyc_status === "DECLINED") {
      setKycResult({
        title: "Não foi possível validar sua identidade",
        message:
          user.kyc_failure_message ||
          "Sua verificação foi recusada. Verifique se você enviou o documento físico original.",
        type: "error",
      });

      setShowKycResultModal(true);
    }

    if (user?.kyc_status === "APPROVED") {
      setKycResult({
        title: "Identidade verificada com sucesso",
        message: "Seu pagamento já pode continuar.",
        type: "success",
      });

      setShowKycResultModal(true);
    }

    setKycFinished(false);
  }, [kycFinished, user?.kyc_status, user?.kyc_failure_message]);

  useEffect(() => {
    function handleMessage(event) {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "DIDIT_KYC_DONE") {
        loadUser();
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [loadUser]);

  useEffect(() => {
    const payment_service = sessionStorage.getItem("payment_service");

    if (location.state?.item?.service_payment_id || payment_service === "1") {
      setPaymentCompleted(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (!isLoaded || !window.google) return;

    const restoredOrigin = buildStoredStop(storedTrip, "origem");
    const restoredDestination = buildStoredStop(storedTrip, "destino");

    if (restoredOrigin) {
      setOriginPos(restoredOrigin.position);
    }

    if (restoredDestination) {
      setDestinationPos(restoredDestination.position);
      setMapCenter(restoredDestination.position);
    }
  }, [isLoaded, storedTrip]);

  useEffect(() => {
    if (!originPos || !destinationPos || !window.google) return;

    if (storedTrip?.meio_transporte === "aviao") {
      setDirections(null);
      return;
    }

    const DirectionsService = new window.google.maps.DirectionsService();

    DirectionsService.route(
      {
        origin: originPos,
        destination: destinationPos,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: true,
      },
      (result, status) => {
        if (status === "OK" && result?.routes?.length) {
          setDirections(result);
        } else {
          console.error("Erro ao gerar rota:", status);
        }
      },
    );
  }, [originPos, destinationPos, storedTrip]);

  useEffect(() => {
    if (!directions?.routes?.length) return;

    const options = directions.routes.map((route, index) => {
      const durationSeconds = getRouteTotalDurationSeconds(route);

      return {
        key: `opcao-rota-${index}`,
        index,
        durationSeconds,
        durationText: formatDurationPt(durationSeconds),
        rawRoute: route,
      };
    });

    setRouteOptions(options);
  }, [directions]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    let cancelled = false;

    (async () => {
      const { AdvancedMarkerElement } =
        await window.google.maps.importLibrary("marker");

      if (cancelled) return;

      if (originMarkerRef.current) {
        originMarkerRef.current.map = null;
      }

      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.map = null;
      }

      const map = mapRef.current;

      if (originPos) {
        originMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: originPos,
          title: "Origem",
          content: createMarkerContent("A"),
        });
      }

      if (destinationPos) {
        destinationMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: destinationPos,
          title: "Destino",
          content: createMarkerContent("B"),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, originPos, destinationPos]);

  useEffect(() => {
    if (
      !isAirplane ||
      !isLoaded ||
      !mapRef.current ||
      !originPos ||
      !destinationPos ||
      !window.google
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    bounds.extend(originPos);
    bounds.extend(destinationPos);

    mapRef.current.fitBounds(bounds, 80);
  }, [isAirplane, isLoaded, originPos, destinationPos]);

  const startKyc = async () => {
    try {
      setLoadingKyc(true);

      const response = await authFetch(
        `${API_URL}/api/auth/kyc/create-session/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            serie_trip_negotiations:
              location?.state?.item?.serie_trip_negotiations,
            return_to: "/payment-client",
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Erro ao iniciar KYC");
      }

      const data = await response.json();

      const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(
        navigator.userAgent,
      );

      if (isMobile) {
        // No celular abre a página da Didit
        window.location.href = data.url;
        return;
      }

      // Desktop continua usando a modal
      DiditSdk.shared.startVerification({
        url: data.url,
        onComplete: () => {
          loadUser();
        },
        onCancel: () => {
          console.log("Verificação cancelada");
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingKyc(false);
    }
  };

  const handlePixPayment = async () => {
    setShowPix(true);
    await paymentService(location, "pix");
  };

  const handleCopyPix = async () => {
    if (!pixData?.copyPaste) return;

    await navigator.clipboard.writeText(pixData.copyPaste);

    setPixCopied(true);

    setTimeout(() => {
      setPixCopied(false);
    }, 2000);
  };

  const handleCardChange = (field, value) => {
    setCardData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFriendCardDataChange = (field, value) => {
    setFriendCardData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreditCardPayment = async () => {
    await paymentService(location, "credit_card", {
      card: {
        number: cardData.number.replace(/\s/g, ""),
        holderName: cardData.holderName,
        expiryMonth: cardData.expiry.split("/")[0],
        expiryYear: `20${cardData.expiry.split("/")[1]}`,
        ccv: cardData.cvv,
      },
      holder: {
        name: cardHolderDifferent
          ? friendCardData.nome
          : location.state?.item.client?.nome,
        cpfCnpj: cardHolderDifferent
          ? friendCardData.cpf
          : location.state?.item.client?.cpf || "",
        phone: cardHolderDifferent
          ? friendCardData.telefone
          : location.state?.item.client?.telefone || "",
        email: location.state?.item.client?.email || "",
        postalCode: "",
        addressNumber: "",
        addressComplement: "",
        mobilePhone: cardHolderDifferent
          ? friendCardData.telefone
          : location.state?.item.client?.telefone || "",
      },
    });
  };

  return (
    <>
      {showKycWarningModal && (
        <div className="kyc-modal-backdrop">
          <div className="kyc-modal-card">
            <h3>Antes de continuar</h3>
            <p>
              Não use capturas de tela, fotos de outro dispositivo, cópias
              impressas ou imagens enviadas por mensagem. Use o documento físico
              original e garanta boa iluminação.
            </p>

            <div className="kyc-modal-actions">
              <button
                className="kyc-modal-btn secondary"
                onClick={() => setShowKycWarningModal(false)}
              >
                Cancelar
              </button>

              <button
                className="kyc-modal-btn primary"
                onClick={async () => {
                  setShowKycWarningModal(false);
                  await startKyc();
                }}
                disabled={loadingKyc}
              >
                {loadingKyc ? "Abrindo..." : "Continuar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showKycResultModal && kycResult && (
        <div className="kyc-modal-backdrop">
          <div className="kyc-modal-card">
            <h3>{kycResult.title}</h3>
            <p>{kycResult.message}</p>

            <div className="kyc-modal-actions">
              <button
                className="kyc-modal-btn primary"
                onClick={() => setShowKycResultModal(false)}
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="payment-dashboard-container">
        <style>{`
          :root {
            --bg-main: #f6f8fc;
            --bg-card: #ffffff;
            --text-main: #0f172a;
            --text-muted: #64748b;
            --primary: #2563eb;
            --primary-2: #0f7cff;
            --accent: #06b6d4;
            --accent-hover: #0891b2;
            --border-color: #e2e8f0;
            --radius-lg: 26px;
            --radius-md: 16px;
            --radius-sm: 12px;
            --shadow-sm: 0 6px 18px rgba(15, 23, 42, 0.06);
            --shadow-md: 0 12px 28px rgba(15, 23, 42, 0.10);
            --shadow-lg: 0 24px 50px rgba(15, 23, 42, 0.12);
          }

          .kyc-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .kyc-modal-card {
          width: 100%;
          max-width: 520px;
          background: #fff;
          border-radius: 24px;
          padding: 28px;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.18);
        }

        .kyc-modal-card h3 {
          margin: 0 0 12px;
          font-size: 22px;
          font-weight: 900;
          color: #0f172a;
        }

        .kyc-modal-card p {
          margin: 0;
          color: #475569;
          line-height: 1.7;
          font-size: 15px;
        }

        .kyc-modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
          flex-wrap: wrap;
        }

        .kyc-modal-btn {
          border: none;
          border-radius: 14px;
          padding: 12px 18px;
          font-weight: 800;
          cursor: pointer;
        }

        .kyc-modal-btn.primary {
          background: linear-gradient(135deg, #2563eb, #06b6d4);
          color: white;
        }

        .kyc-modal-btn.secondary {
          background: #e2e8f0;
          color: #0f172a;
        }

        .kyc-modal-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

          .payment-dashboard-container {
            position: relative;
            color: var(--text-main);
            min-height: 100vh;
            padding: 24px;
            box-sizing: border-box;
            
          }

          .payment-dashboard-container * {
            box-sizing: border-box;
          }

          .max-width-wrapper {
            max-width: 1240px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 24px;
          }

          .top-summary-layout {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 320px;
            gap: 24px;
            align-items: stretch;
            width: 100%;
          }

          .trip-summary-bar {
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 0px 30px;
            border: 1px solid rgba(219, 234, 254, 0.95);
            border-radius: var(--radius-lg);
            background:
              linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98));
            box-shadow: var(--shadow-md);
          }

          .info-box-travel {
            width: 100%;
            display: flex;
            flex-direction: column;       
            margin-top: 20px;
            min-width: 0;
          }

          .route-tags-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            width: 100%;
            min-width: 0;
          }

          .route-container {
            display: flex;
            gap: 12px;
            width: 170%;
            padding-left: 0px;
          }

          .route-container::before {
            display: none
          }

          .location-badge {
            width: auto;
            font-weight: 800;
          }

          .icon-box {
            margin-top: 0px;
            margin-right: 10px;
            margin-left: 10px;
           
          }

          .tags {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 10px;
            flex-wrap: wrap;
            margin-left: auto;
          }

          .tag-item {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 10px 12px;
            border-radius: 999px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            color: #334155;
            font-size: 13px;
            font-weight: 700;
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
            white-space: nowrap;
          }

          .tag-icon {
            color: #fff;
            flex-shrink: 0;
          }

          .dates {
            display: flex;
            flex-wrap: wrap; 
            gap: 14px;
            width: 100%;
          }

          .dates::after {
            display: none;
          }

          .date-content {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            min-width: 0;
          }

          .date-group small {
            font-weight: 800
          }

          .blue-icon {
            color: #0f7cff;
            flex-shrink: 0;
          }

          .date-text {
            font-size: 14px;
            color: #007bff;
            font-weight: 700;
            min-width: 0;
            overflow-wrap: anywhere;
          }

          .time-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            border-radius: 999px;
            color: #007bff;
            font-size: 12px;
            font-weight: 700;
            margin-left: -5px;
            white-space: nowrap;
          }

          .main-workspace-grid {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            gap: 24px;
            align-items: start;
          }

          .payment-card {
            width: 100%;
            height: 100%;
            flex-shrink: 0;
            position: relative;
            overflow: hidden;
            display: flex;
            align-items: center;
            border: 1px solid rgba(219, 234, 254, 0.95);
            border-radius: var(--radius-lg);
            padding: 26px;
            background:
              linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98));
            box-shadow: var(--shadow-md);
          }

          .payment-summary-list {
            display: flex;
            flex-direction: column;
            gap: 14px;
            position: relative;
            width: 100%;
          }

          .payment-summary-list .payment-row:nth-child(1) {
            border: 1px solid rgba(16, 185, 129, 0.35);
            background: linear-gradient(135deg, #ecfdf5, #ffffff);
            box-shadow: 0 12px 30px rgba(16, 185, 129, 0.12);
          }

          .payment-summary-list .payment-row:nth-child(1) strong {
            background: linear-gradient(135deg, #10b981, #047857);
            color: #fff;
            border: none;
            box-shadow: 0 10px 20px rgba(16, 185, 129, 0.25);
          }

          .payment-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 10px 20px;
            border-radius: 20px;
            background: linear-gradient(135deg, #ffffff, #f8fbff);
            border: 1px solid #e2e8f0;
            box-shadow: 0 10px 25px rgba(15, 23, 42, 0.05);
            transition: all 0.25s ease;
            position: relative;
            overflow: hidden;
          }

          .payment-row::before {
            content: "";
            position: absolute;
            inset: 0;
            background: radial-gradient(
              circle at top left,
              rgba(37, 99, 235, 0.08),
              transparent 60%
            );
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .payment-row:hover::before {
            opacity: 1;
          }

          .payment-row strong {
            font-size: 13px;
            font-weight: 900;
            color: #0f7cff;
            padding: 8px 12px;
            border-radius: 12px;
            white-space: nowrap;
          }

          .payment-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
            min-width: 0;
          }

          .payment-label {
            font-size: 13px;
            font-weight: 800;
            color: #334155;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .payment-success-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 30px;
          }

          .payment-success-card {
            max-width: 620px;
            width: 100%;
            background: #fff;
            border-radius: 26px;
            padding: 55px;
            text-align: center;
            box-shadow: 0 25px 60px rgba(0,0,0,.08);
          }

          .payment-success-icon {
            width: 150px;
            height: 150px;
            margin: auto;
            border-radius: 999px;
            background: #ecfdf3;
            color: #22c55e;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 25px;
          }

          .payment-success-card h1 {
            color: #111827;
            font-size: 30px;
            margin-bottom: 14px;
          }

          .payment-success-card p {
            color: #6b7280;
            font-size: 18px;
          }

          .payment-success-note {
            margin-top: 30px;
            background: #f0fdf4;
            color: #15803d;
            padding: 18px;
            border-radius: 14px;
            font-weight: 600;
          }

          .form-card {
            padding: 32px;
            border-radius: var(--radius-lg);
            border: 1px solid rgba(226, 232, 240, 0.95);
            box-shadow: var(--shadow-md);
            position: relative;
            overflow: hidden;
            background:
              linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98));
          }

          .form-card > * {
            position: relative;
            z-index: 1;
          }

          .form-card h2 {
            font-size: 22px;
            font-weight: 800;
            margin: 0 0 20px 0;
            padding-bottom: 12px;
            text-align: center;
            color: #0f172a;
            letter-spacing: -0.02em;
          }

          .tabs-selector {
            display: flex;
            gap: 8px;
            padding: 6px;
            border-radius: 18px;
            background: #eef4ff;
            border: 1px solid #dbeafe;
            margin-bottom: 24px;
          }

          .tab-btn {
            flex: 1;
            background: transparent;
            border: none;
            padding: 12px 14px;
            font-size: 14px;
            font-weight: 700;
            color: #64748b;
            cursor: pointer;
            border-radius: 14px;
            transition: all 0.22s ease;
          }

          .tab-btn:hover {
            color: #0f172a;
            background: rgba(255,255,255,0.72);
          }

          .tab-btn.active {
            background: linear-gradient(135deg, #2563eb, #06b6d4);
            color: #fff;
            box-shadow: 0 10px 20px rgba(37, 99, 235, 0.18);
          }

          .form-fields-stack {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .input-group {
            display: flex;
            flex-direction: column;
            gap: 6px;
            position: relative;
          }

          .input-group label {
            font-size: 12px;
            font-weight: 700;
            color: #475569;
            letter-spacing: 0.02em;
            text-transform: uppercase;
          }

          .input-with-icon {
            position: relative;
            display: flex;
            align-items: center;
          }

          .input-with-icon input {
            width: 100%;
            border: 1px solid #dbe4f0;
            outline: none;
            border-radius: 16px;
            padding: 13px 16px 13px 42px;
            font-size: 14px;
            background: #fff;
            transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
            color: #0f172a;
          }

          .input-with-icon input::placeholder {
            color: #94a3b8;
          }

          .input-with-icon input:focus {
            border-color: #60a5fa;
            box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.08);
          }

          .input-icon-left {
            position: absolute;
            left: 14px;
            color: #0f7cff;
            width: 18px;
            height: 18px;
            pointer-events: none;
          }

          .card-brands-indicator {
            position: absolute;
            right: 14px;
            display: flex;
            gap: 2px;
          }

          .brand-circle {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            opacity: 0.85;
          }

          .brand-red {
            background: #ef4444;
            margin-right: -8px;
          }

          .brand-yellow {
            background: #eab308;
          }

          .input-row-split {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }

          .checkbox-holder-box {
            border: 1px solid #dbe4f0;
            background: linear-gradient(180deg, #ffffff, #f8fbff);
            border-radius: 18px;
            padding: 16px;
            display: flex;
            gap: 12px;
            align-items: flex-start;
            box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
            transition: border-color 0.2s, box-shadow 0.2s;
          }

          .checkbox-holder-box:hover {
            border-color: #bfdbfe;
            box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
          }

          .checkbox-holder-box input[type="checkbox"] {
            margin-top: 3px;
            width: 18px;
            height: 18px;
            accent-color: #2563eb;
            cursor: pointer;
            flex-shrink: 0;
          }

          .checkbox-holder-copy {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .checkbox-holder-copy strong {
            font-size: 14px;
            color: #0f172a;
            font-weight: 700;
          }

          .checkbox-holder-copy span {
            font-size: 13px;
            color: #64748b;
            line-height: 1.45;
          }

          .expanded-friend-form {
            margin-top: 2px;
            padding: 18px;
            border-radius: 18px;
            background: #f8fbff;
            border: 1px solid #dbeafe;
            display: grid;
            gap: 14px;
            animation: expandCard 0.22s ease-out;
          }

          @keyframes expandCard {
            from {
              opacity: 0;
              transform: translateY(-6px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .expanded-friend-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 2px;
          }

          .expanded-friend-title svg {
            color: #0f7cff;
          }

          .map-section-wrapper {
            display: flex;
            flex-direction: column;
            gap: 16px;
            height: 100%;
          }

          .map-container-frame {
            background: var(--bg-card);
            border-radius: var(--radius-lg);
            border: 1px solid var(--border-color);
            height: 520px;
            overflow: hidden;
            box-shadow: var(--shadow-md);
          }

          .map-placeholder-loading {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--text-muted);
            background: #f1f5f9;
            border-radius: calc(var(--radius-lg) - 8px);
          }

          .security-notice-box {
            background: linear-gradient(180deg, #eff6ff, #f8fbff);
            border: 1px solid #bfdbfe;
            border-radius: var(--radius-md);
            padding: 16px;
            font-size: 13px;
            color: #1e40af;
            line-height: 1.5;
            box-shadow: var(--shadow-sm);
          }

          .security-notice-box strong {
            color: #1d4ed8;
          }

          .custom-marker {
            background: #2563eb;
            color: #ffffff;
            font-weight: bold;
            border-radius: 50%;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 6px rgba(0,0,0,0.2);
            border: 2px solid #ffffff;
          }

          .pix-wrapper {
            width: 100%;
          }

          .pix-start,
          .pix-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 18px;
            padding: 28px;
            border-radius: 24px;
            background:
              radial-gradient(circle at top, rgba(16,185,129,.08), white);
            border: 1px solid rgba(16,185,129,.12);
            animation: fadeUp .35s ease;
          }

          .pix-title {
            margin: 0;
            font-size: 22px;
            font-weight: 800;
            color: #111827;
          }

          .pix-description,
          .pix-helper {
            margin: 0;
            text-align: center;
            color: #64748b;
            line-height: 1.6;
            max-width: 320px;
          }

          .show-pix-btn {
            width: 100%;
            padding: 16px;
            border: none;
            border-radius: 20px;
            cursor: pointer;
            font-size: 15px;
            font-weight: 800;
            color: white;
            background: linear-gradient(135deg, #10b981, #047857);
            box-shadow: 0 14px 30px rgba(16,185,129,.28);
            transition: .25s;
          }

          .show-pix-btn:hover {
            transform: translateY(-2px) scale(1.01);
          }

          .show-pix-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }

          .pix-qrcode-box {
            background: white;
            padding: 24px;
            border-radius: 24px;
            box-shadow: 0 20px 40px rgba(0,0,0,.08);
          }

          .pix-code-box {
            width: 100%;
            background: #f8fafc;
            border: 1px dashed #94a3b8;
            padding: 14px;
            border-radius: 14px;
            font-size: 12px;
            color: #334155;
            word-break: break-all;
          }

          .copy-pix-btn {
            width: 100%;
            border: none;
            padding: 14px;
            border-radius: 16px;
            cursor: pointer;
            font-weight: 800;
            color: white;
            background: linear-gradient(135deg, #111827, #1f2937);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }

          .pix-success {
            width: 100%;
            padding: 12px;
            border-radius: 14px;
            background: #dcfce7;
            color: #166534;
            font-weight: 700;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
          }

          @keyframes fadeUp {
            from {
              opacity: 0;
              transform: translateY(18px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .modal-error {
            padding: 14px 16px;
            border-radius: 14px;
            background: #fef2f2;
            border: 1px solid #fecaca;
            color: #b91c1c;
            font-weight: 600;
            margin-bottom: 16px;
          }

          .payment-processing-box {
            margin: 12px 0 16px;
            padding: 12px 14px;
            border-radius: 12px;
            background: #eef6ff;
            border: 1px solid #cfe3ff;
            color: #1d4ed8;
            font-size: 14px;
          }

          @media (max-width: 981px) {
              body:not(.home-page) .location-badge{
                font-size: min(4.5vw, 24px);
              }

              body:not(.home-page) .dates {
                width: 110%;
                margin-top: 0px;
              }

              body:not(.home-page) .date-group:nth-child(2) {
                position: relative;
                right: 20px;
              }   
                
              body:not(.home-page) .time-badge {
                color: #007bff;
              }
          }

          @media (max-width: 978px) { 
            .info-box-travel {     
              margin-top: 10px;
            }
          }

          @media (max-width: 979px) {
            .top-summary-layout {
              grid-template-columns: 1fr;
              margin-top: -20px
            }

            .route-tags-row {
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
            }

            .tags {
              width: 100%;
              margin-left: 0;
              justify-content: flex-start;
            }

            .dates {
              grid-template-columns: 1fr;
            }

            .map-container-frame {
              height: 360px;
            }
          }

          @media (min-width: 980px) {
            .route-tags-row {
              flex-direction: row;
              align-items: center;
            }

            .route-tags-row .tags {
              justify-content: flex-end;
            }
          }

          @media (min-width: 991px) {
            .main-workspace-grid {
              grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            }

            .payment-card {
              align-self: start;
            }

            .map-container-frame {
              height: 100%;
            }
          }

          @media (min-width: 1060px) {
            .top-summary-layout {
              height: 130px
            }

            .dates {
              margin-top: -25px
            }

            .payment-row {
              padding: 5px 20px
            }
          }

          @media (min-width: 305px) and (max-width: 720px) {
            .payment-info {
                position: relative;
                top: 6px;
              }

             .payment-row strong {
                margin-left: auto !important;
                margin-top: -30px;
                margin-bottom: -3px;
             }
          }

          @media (max-width: 720px) {              
            .payment-dashboard-container {
              padding: 16px;
            }

            .form-card {
              padding: 22px;
            }

            .tabs-selector {
              padding: 4px;
              flex-direction: column;
            }

            .tab-btn {
              width: 100%;
              padding: 12px 10px;
              font-size: 13px;
            }

            .input-row-split {
              grid-template-columns: 1fr;
            }

            .payment-card {
              padding: 18px;
            }

            .payment-row {
              flex-direction: column;
              align-items: flex-start;
            }

            .payment-row strong {
              margin-left: 0;
            }

            .pix-start,
            .pix-content {
              padding: 20px;
            }

            .map-container-frame {
              height: 300px;
            }
          }

          @media (max-width: 282px) {
              body:not(.home-page) .date-group:nth-child(2) {
                  position: relative;
                  right: 0px;
              }
          }

          @media (min-width: 400px) and (max-width: 980px) {              
              body:not(.home-page) .date-group:nth-child(2) {
                position: relative;
                right: 8vw;
              }                 
          }

          @media (max-width: 520px) {
            .trip-summary-bar {
              padding-left: 18px;
              padding-right: 18px;
            }

            .info-box-travel {
              margin-left: -5px;
              gap: 10px;
            }

            body:not(.home-page) .tags {
              margin-top: 0px !important
            }

            .tag-item {
              width: 100%;
              justify-content: flex-start;
            }

             body:not(.home-page) .dates {
                margin-top: -5px;
              }

            .date-content {
              gap: 6px;
            }

            .form-card h2 {
              font-size: 20px;
            }
          }
        `}</style>

        <div className={`max-width-wrapper ${menuOpen ? "menu-open" : ""}`}>
          <SidebarClient menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

          <div className="dashboard-header">
            <h1>Dashboard do Cliente</h1>
            <p className="breadcrumb">
              <a href="/dashboard-client">Início</a> / Pagamento do Serviço
            </p>
          </div>

          <div className="top-summary-layout">
            <div className="trip-summary-bar">
              <div className="info-box-travel">
                <div className="route-tags-row">
                  <div className="route-container">
                    <div className="location-badge">
                      {storedTrip?.cidade_origem}
                    </div>

                    <div className="icon-box">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#1cb5e0"
                        strokeWidth="3"
                      >
                        <path d="M7 7l10 10M17 7v10H7" />
                      </svg>
                    </div>

                    <div className="location-badge">
                      {storedTrip?.cidade_destino}
                    </div>
                  </div>

                  <div className="tags">
                    <div className="tags-row">
                      <span className="tag-item">
                        <Car size={16} strokeWidth={2.5} className="tag-icon" />
                        {storedTrip?.meio_transporte}
                      </span>

                      <span className="tag-item">
                        <CalendarDays
                          size={16}
                          strokeWidth={2.5}
                          className="tag-icon"
                        />
                        {storedTrip?.duracao_viagem}
                      </span>
                    </div>

                    <span className="tag-item">
                      <Briefcase
                        size={16}
                        strokeWidth={2.5}
                        className="tag-icon"
                      />
                      {location.state?.item?.client_order_type}
                    </span>
                  </div>
                </div>

                <div className="dates">
                  <div className="date-group">
                    <small>SAÍDA</small>
                    <div className="date-content">
                      <CalendarCheck size={13} className="blue-icon" />
                      <span className="date-text">
                        {storedTrip?.data_partida} às
                      </span>
                      <div className="time-badge">
                        <span>{storedTrip?.horas_partida || "-"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="date-group">
                    <small>CHEGADA</small>
                    <div className="date-content">
                      <CalendarCheck size={13} className="blue-icon" />
                      <span className="date-text">
                        {storedTrip?.data_chegada || "-"} às
                      </span>
                      <div className="time-badge">
                        <span>{storedTrip?.horas_chegada || "-"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="payment-card">
              <div className="payment-summary-list">
                <div className="payment-row">
                  <div className="payment-info">
                    <span className="payment-label">Valor do serviço</span>
                  </div>

                  <strong>{location.state?.item?.order_value}</strong>
                </div>

                <div className="payment-row">
                  <div className="payment-info">
                    <span className="payment-label">Valor do seguro</span>
                  </div>

                  <strong>{location.state?.item?.insurance_amount}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="main-workspace-grid">
            {!kycVerified ? (
              <div className="form-card">
                <KycVerification
                  loading={loadingKyc}
                  onOpenWarning={() => setShowKycWarningModal(true)}
                />
              </div>
            ) : (
              <div className="form-card">
                {paymentCompleted ? (
                  <div className="payment-success-card">
                    <div className="payment-success-icon">
                      <CheckCircle2 size={95} strokeWidth={2.3} />
                    </div>

                    <h1>Pagamento realizado com sucesso</h1>

                    <div className="payment-success-note">
                      Recebemos o pagamento do serviço da sua encomenda.
                    </div>
                  </div>
                ) : (
                  <>
                    <h2>Pagamento do Serviço</h2>

                    {errorPayment && (
                      <div className="modal-error">{errorPayment}</div>
                    )}

                    {(loadingPayment || paymentProcessing) && (
                      <div className="payment-processing-box">
                        {paymentMessage || "Processando pagamento..."}
                      </div>
                    )}

                    <div className="tabs-selector">
                      <button
                        onClick={() => setPaymentMethod("credit_card")}
                        className={`tab-btn ${
                          paymentMethod === "credit_card" ? "active" : ""
                        }`}
                      >
                        Cartão de Crédito
                      </button>

                      <button
                        onClick={() => setPaymentMethod("pix")}
                        className={`tab-btn ${
                          paymentMethod === "pix" ? "active" : ""
                        }`}
                      >
                        PIX
                      </button>
                    </div>

                    {paymentMethod === "credit_card" && (
                      <form
                        className="form-fields-stack"
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleCreditCardPayment();
                        }}
                      >
                        <div className="input-group">
                          <label>Número do Cartão</label>

                          <div className="input-with-icon">
                            <input
                              value={cardData.number}
                              onChange={(e) =>
                                handleCardChange("number", e.target.value)
                              }
                              placeholder="0000 0000 0000 0000"
                            />

                            <CreditCard className="input-icon-left" />

                            <div className="card-brands-indicator">
                              <span className="brand-circle brand-red"></span>
                              <span className="brand-circle brand-yellow"></span>
                            </div>
                          </div>
                        </div>

                        <div className="input-group">
                          <label>Nome do Titular do Cartão</label>

                          <div className="input-with-icon">
                            <input
                              value={cardData.holderName}
                              onChange={(e) =>
                                handleCardChange("holderName", e.target.value)
                              }
                            />

                            <User size={11} className="input-icon-left" />
                          </div>
                        </div>

                        <div className="input-row-split">
                          <div className="input-group">
                            <label>Data de Expiração</label>

                            <div className="input-with-icon">
                              <input
                                placeholder="MM/AA"
                                value={cardData.expiry}
                                onChange={(e) =>
                                  handleCardChange("expiry", e.target.value)
                                }
                              />

                              <Calendar className="input-icon-left" />
                            </div>
                          </div>

                          <div className="input-group">
                            <label>Código CVV</label>

                            <div className="input-with-icon">
                              <input
                                type="password"
                                placeholder="CVV"
                                value={cardData.cvv}
                                onChange={(e) =>
                                  handleCardChange("cvv", e.target.value)
                                }
                              />

                              <Lock className="input-icon-left" />
                            </div>
                          </div>
                        </div>

                        <label className="checkbox-holder-box">
                          <input
                            type="checkbox"
                            checked={cardHolderDifferent}
                            onChange={(e) =>
                              setCardHolderDifferent(e.target.checked)
                            }
                          />

                          <div className="checkbox-holder-copy">
                            <strong>
                              Os dados do titular do cartão são diferentes dos
                              dados da minha conta
                            </strong>
                            <span>
                              Marque esta opção para informar os dados do
                              amigo/dono do cartão.
                            </span>
                          </div>
                        </label>

                        {cardHolderDifferent && (
                          <>
                            <div className="input-group">
                              <label>Nome</label>

                              <div className="input-with-icon">
                                <input
                                  placeholder="Nome"
                                  value={friendCardData.nome}
                                  onChange={(e) =>
                                    handleFriendCardDataChange(
                                      "nome",
                                      e.target.value,
                                    )
                                  }
                                />

                                <User size={11} className="input-icon-left" />
                              </div>
                            </div>

                            <div className="input-group">
                              <label>CPF</label>

                              <div className="input-with-icon">
                                <input
                                  placeholder="CPF"
                                  value={friendCardData.cpf}
                                  onChange={(e) =>
                                    handleFriendCardDataChange(
                                      "cpf",
                                      e.target.value,
                                    )
                                  }
                                />

                                <CreditCard
                                  size={11}
                                  className="input-icon-left"
                                />
                              </div>
                            </div>

                            <div className="input-group">
                              <label>Telefone</label>

                              <div className="input-with-icon">
                                <input
                                  placeholder="Telefone"
                                  value={friendCardData.telefone}
                                  onChange={(e) =>
                                    handleFriendCardDataChange(
                                      "telefone",
                                      e.target.value,
                                    )
                                  }
                                />

                                <Phone size={11} className="input-icon-left" />
                              </div>
                            </div>
                          </>
                        )}

                        <button
                          type="submit"
                          className="show-pix-btn"
                          disabled={loadingPayment || paymentProcessing}
                        >
                          {loadingPayment ? "Processando..." : "Pagar"}
                        </button>
                      </form>
                    )}

                    {paymentMethod === "pix" && (
                      <div className="pix-wrapper">
                        {!showPix ? (
                          <div className="pix-start">
                            <button
                              className="show-pix-btn"
                              onClick={handlePixPayment}
                              disabled={loadingPayment || paymentProcessing}
                            >
                              <QrCode size={34} />

                              <div style={{ marginTop: "0px" }}>
                                {loadingPayment
                                  ? "Gerando..."
                                  : "Gerar QR CODE"}
                              </div>
                            </button>
                          </div>
                        ) : (
                          <div className="pix-content">
                            {pixData?.qrcode && (
                              <img
                                src={`data:image/png;base64,${pixData.qrcode}`}
                                alt="PIX"
                                style={{ width: 220, maxWidth: "100%" }}
                              />
                            )}

                            <div className="pix-code-box">
                              {pixData?.copyPaste}
                            </div>

                            <button
                              onClick={handleCopyPix}
                              className="copy-pix-btn"
                            >
                              <Copy size={18} />
                              Copiar código
                            </button>

                            {pixCopied && (
                              <div className="pix-success">
                                <CheckCircle2 />
                                Código copiado
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="map-section-wrapper">
              <div className="map-container-frame">
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "16px",
                    }}
                    center={mapCenter || defaultCenter}
                    zoom={10}
                    options={{
                      mapId,
                      disableDefaultUI: false,
                      zoomControl: true,
                      streetViewControl: false,
                      fullscreenControl: true,
                    }}
                    onLoad={(map) => {
                      mapRef.current = map;
                    }}
                  >
                    {isAirplane && originPos && destinationPos && (
                      <Polyline
                        path={[originPos, destinationPos]}
                        options={{
                          strokeColor: "#1e88e5",
                          strokeWeight: 4,
                          strokeOpacity: 0.8,
                        }}
                      />
                    )}

                    {!isAirplane && directions && routeOptions.length > 0 && (
                      <DirectionsRenderer
                        directions={directions}
                        options={{
                          routeIndex: Math.min(
                            Math.max(selectedRouteIndex, 0),
                            routeOptions.length - 1,
                          ),
                          suppressMarkers: true,
                          polylineOptions: {
                            strokeColor: "#2563eb",
                            strokeWeight: 5,
                            strokeOpacity: 0.92,
                          },
                        }}
                      />
                    )}
                  </GoogleMap>
                ) : (
                  <div className="map-placeholder-loading">
                    Carregando mapa interativo...
                  </div>
                )}
              </div>
              <div className="security-notice-box">
                💡 <strong>Fique tranquilo:</strong> o valor do pagamento só
                será definitivamente debitado do seu cartão quando o viajante
                aceitar levar a sua encomenda.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentService;
